import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewEncapsulation
} from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortable, Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { UcrSecurityService } from '../core/ucr-security/ucr-security.service';
import { AlertService } from '../shared/services/alert.service';
import { FileDownloadService } from '../shared/services/file-download.service';
import { ReflectionService } from '../shared/utilities/reflection.service';
import { ExportConfiguration, FilterState, TableState, TableStateService } from './table-state.service';
import { TableService } from './table.service';
import {
    ColumnDefinition,
    ColumnExportDefinition,
    FilterSearchCriteria,
    PagingSort,
    TableCellResponse,
    TableCriteria,
    TableFilter,
    TableMap,
    TableSearchCriteria
} from './table.types';

@Component({
    selector: 'app-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    providers: [TableService]
})
export class TableComponent implements OnInit, OnDestroy {
    @Input() tableName: string;
    @Input() baseUrl: string;
    @Input() columns: Array<ColumnDefinition>;
    @Input() clientSortAndPage: boolean = false;
    @Input() searchCriteria: TableSearchCriteria;
    @Input() what: string = 'elements';
    @Input() loadImmediate: boolean = true;
    @Input() elementIdField: string;
    @ViewChild(MatSort) matSort: MatSort;
    @ViewChildren(MatPaginator) matPaginatorQueryList: QueryList<MatPaginator>;
    private tempFilterUserName: string;

    get ready(): boolean {
        return !this.loading && this.filteredColumns?.length > 0;
    }

    get pageLabel(): string {
        return `Select page of ${this.what}`;
    }

    pageSizeOptions: Array<number> = [10, 25, 50, 100, 250];
    loading: boolean = true;
    displayedColumns: Array<string>;
    filteredColumns: Array<ColumnDefinition> = [];
    dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

    private criteria: TableCriteria = {
        sortBy: '',
        sortDirection: '',
        max: 10,
        offset: 0
    };
    private stateSubscription: Subscription;
    private filterSubscription: Subscription;
    private updateSelect: boolean;
    private exportSubscription$: Subscription;
    private loadData$: Subscription;
    private firstTimeSorting: boolean;

    constructor(
        private tableService: TableService,
        private alert: AlertService,
        private reflection: ReflectionService,
        private tableState: TableStateService,
        private fileDownload: FileDownloadService,
        private security: UcrSecurityService
    ) {
        this.security.getUserInfo().subscribe((ui) => {
            this.tempFilterUserName = this.reflection.isNotNullOrEmpty(ui?.username) ? ui.username : '';
        });
    }

    ngOnInit(): void {
        this.tableService.setUrl(this.baseUrl);
        this.filterColumns(true);
        this.initSearchCriteria();
        if (this.loadImmediate) {
            this.loadData(true);
        } else {
            this.loading = false;
            this.tableState.sendFromTable({
                loading: this.loading,
                tableName: this.tableName,
                hasData: false,
                notImmediateLoad: true
            });
        }
        this.subscribe();
    }

    ngOnDestroy() {
        if (this.stateSubscription) {
            this.stateSubscription.unsubscribe();
        }
        if (this.filterSubscription) {
            this.filterSubscription.unsubscribe();
        }
        if (this.exportSubscription$) {
            this.exportSubscription$.unsubscribe();
        }
        if (this.loadData$) {
            this.loadData$.unsubscribe();
        }
    }

    private initSearchCriteria() {
        if (this.searchCriteria) {
            for (const key in this.searchCriteria) {
                this.criteria[key] = this.searchCriteria[key];
            }
        }
    }

    private retrievePaginator(): MatPaginator {
        return this.matPaginatorQueryList.first;
    }

    private initStateSubscription() {
        this.stateSubscription = this.tableState.toTable.subscribe((state: TableState) => {
            if (state.tableName === this.tableName) {
                if (state.update === true) {
                    this.updateElement(state.element);
                } else if (state.remove === true) {
                    this.removeElement(state.element);
                    if (!state.doNotReloadDataOnDelete) {
                        if (this.dataSource.data.length === 0) {
                            let pageIndex: number = 0;
                            const paginator = this.retrievePaginator();
                            if (paginator) {
                                pageIndex = paginator.pageIndex > 0 ? paginator.pageIndex - 1 : paginator.pageIndex;
                            }
                            this.onPage({
                                length: paginator ? paginator.length : 0,
                                pageIndex: pageIndex,
                                pageSize: paginator ? paginator.pageSize : 10
                            });
                            this.matPaginatorQueryList.forEach((m) => (m.pageIndex = pageIndex));
                        } else {
                            this.loadData();
                        }
                    }
                } else if (state.reloadData === true) {
                    this.loadData();
                }
            }
        });
    }

    private initFilterSubscription() {
        this.filterSubscription = this.tableState.fromFilter.subscribe((state: FilterState) => {
            if (state.tableName === this.tableName) {
                if (state.export) {
                    this.exportData(state.filter, state.filterStatementOutput, state.exportConfig);
                } else {
                    this.filterUpdated(state.filter, state.pagingSort);
                }
            }
        });
    }

    private subscribe() {
        this.initStateSubscription();
        this.initFilterSubscription();
    }

    private firstTimeSort(checkDirection: boolean = false) {
        if (this.searchCriteria && this.searchCriteria.sortBy && this.searchCriteria.sortDirection) {
            if (checkDirection && this.matSort.direction === this.searchCriteria.sortDirection) {
                return;
            }
            this.firstTimeSorting = true;
            this.matSort.sort({
                id: this.searchCriteria.sortBy,
                start: this.searchCriteria.sortDirection,
                disableClear: true
            });
            const activeSortHeader: MatSortable = this.matSort.sortables.get(this.searchCriteria.sortBy);
            if (activeSortHeader) {
                activeSortHeader['_setAnimationTransitionState']({
                    fromState: this.searchCriteria.sortDirection,
                    toState: 'active'
                });
            }
        }
    }

    private sendUpdate() {
        this.tableState.sendFromTable({
            loading: this.loading,
            tableName: this.tableName,
            hasData: this.dataSource?.data?.length > 0,
            updateSelect: this.updateSelect
        });
    }

    private exportData(filter: TableFilter, filterOut: string, exportConfig?: ExportConfiguration) {
        this.loading = true;
        this.sendUpdate();
        this.updateCriteria(filter, null);
        const currentOffset = this.criteria.offset;
        const currentMax = this.criteria.max;
        this.criteria.offset = 0;
        this.criteria.max = this.retrievePaginator()?.length;
        const exportColumns: Array<ColumnExportDefinition> = [];
        this.filteredColumns
            .filter((m) => !m.actions || m?.actions?.length === 0)
            .forEach((column: ColumnDefinition) => {
                exportColumns.push({
                    property: column.property,
                    exportPath: column.alternateExportPath,
                    headerLabel: column.headerLabel,
                    isDate: column.isDate,
                    dateFormat: column.exportDateFormat
                });
            });
        this.tableService.setUrl(this.baseUrl);
        if (this.exportSubscription$) {
            this.exportSubscription$.unsubscribe();
        }
        this.exportSubscription$ = this.tableService
            .export({
                criteria: this.criteria,
                columns: exportColumns,
                filterOut: filterOut,
                exportConfig: exportConfig
            })
            .subscribe({
                next: (result) => {
                    this.loading = false;
                    this.sendUpdate();
                    this.fileDownload.downloadFile(result);
                    this.alert.toastSuccess('File downloaded.');
                    this.tableState.sendFromTable({
                        tableName: this.tableName,
                        doneExporting: true,
                        hasData: true
                    });
                    this.criteria.max = currentMax;
                    this.criteria.offset = currentOffset;
                },
                error: (error) => {
                    this.loading = false;
                    this.sendUpdate();
                    if (error.status === 413) {
                        this.alert.toastWarn(
                            'Your export may take a while. We will email it to you once it is complete.'
                        );
                    } else {
                        this.alert.toastError('We encountered an error downloading that spreadsheet.');
                    }
                    console.error(error);
                    this.criteria.max = currentMax;
                    this.criteria.offset = currentOffset;
                }
            });
    }

    loadData(firstLoad: boolean = false, backToFirstPage: boolean = false) {
        if (this.loading && !firstLoad) {
            return;
        }
        if (!this.baseUrl) {
            console.error('Where should I get my data. Please set baseUrl.');
            return;
        }
        if (!this.clientSortAndPage && backToFirstPage) {
            this.criteria.offset = 0;
        }
        this.loading = true;
        this.sendUpdate();
        this.tableService.setUrl(this.baseUrl);
        if (this.loadData$) {
            this.loadData$.unsubscribe();
        }
        let pagingSort: PagingSort = null;
        if (firstLoad) {
            const fs: string = sessionStorage.getItem(`${this.tableName}_${this.tempFilterUserName}_temp_filters`);
            if (this.reflection.isNotNullOrEmpty(fs)) {
                const f: FilterSearchCriteria = JSON.parse(fs);
                if (f?.controls) {
                    Object.keys(f.controls).forEach((k) => {
                        this.criteria[k] = f.controls[k];
                    });
                }
                pagingSort = f.pagingSort;
                this.updatePagingSort(f.pagingSort, false, false);
                if (f?.pagingSort) {
                    backToFirstPage = false;
                }
            }
        } else {
            this.tableState.sendCriteria({
                tableName: this.tableName,
                pageSize: this.retrievePaginator()?.pageSize,
                pageIndex: this.retrievePaginator()?.pageIndex,
                sortBy: this.criteria.sortBy,
                sortDirection: this.criteria.sortDirection
            });
        }
        this.loadData$ = this.tableService.listMap(this.criteria).subscribe(
            (map: TableMap) => {
                if (!map) {
                    map = {
                        list: [],
                        totalCount: 0
                    };
                }
                if (!map.list) {
                    map.list = [];
                }
                this.dataSource = new MatTableDataSource<any>(map.list);
                if (this.clientSortAndPage) {
                    this.dataSource.sort = this.matSort;
                    this.dataSource.paginator = this.matPaginatorQueryList.last;
                } else {
                    this.matPaginatorQueryList.forEach((m) => (m.length = map.totalCount));
                    if (backToFirstPage) {
                        this.matPaginatorQueryList.forEach((m) => (m.pageIndex = 0));
                    }
                }
                this.filterColumns(false);
                this.dataSource._updateChangeSubscription();
                this.loading = false;
                if (pagingSort) {
                    this.updatePagingSort(pagingSort, false);
                }
                if (firstLoad) {
                    this.firstTimeSort();
                    if (!pagingSort && this.searchCriteria?.max > 0) {
                        this.matPaginatorQueryList.forEach((m) => (m.pageSize = this.searchCriteria.max));
                    }
                }
                this.sendUpdate();
            },
            (error) => {
                this.alert.toastErrorObject(error);
                this.loading = false;
                this.sendUpdate();
            }
        );
    }

    private findIndex(element: any) {
        const idField = this.elementIdField ? this.elementIdField : 'id';
        if (element && this.dataSource && this.dataSource.data) {
            return this.dataSource.data.map((x) => x[idField]).indexOf(element[idField]);
        }
        return -1;
    }

    private updateElement(element: any) {
        if (!element) {
            return;
        }
        const index = this.findIndex(element);
        if (index >= 0) {
            this.dataSource.data[index] = element;
        } else {
            this.dataSource.data.push(element);
            this.matPaginatorQueryList.forEach((m) => (m.length = m.length + 1));
        }
        this.dataSource._updateChangeSubscription();
        this.updateSelect = true;
        this.sendUpdate();
        this.updateSelect = false;
    }

    private removeElement(element: any) {
        if (!element) {
            return;
        }
        const index = this.findIndex(element);
        if (index >= 0) {
            this.matPaginatorQueryList.forEach((m) => (m.length = m.length - 1));
            this.dataSource.data.splice(index, 1);
            this.dataSource._updateChangeSubscription();
        }
    }

    onPage(pageEvent: PageEvent) {
        if (!this.clientSortAndPage) {
            this.criteria.max = pageEvent.pageSize;
            this.criteria.offset = pageEvent.pageIndex * pageEvent.pageSize;
            this.matPaginatorQueryList.forEach((m) => {
                m.pageIndex = pageEvent.pageIndex;
                m.pageSize = pageEvent.pageSize;
            });
            this.loadData();
        }
    }

    onSort(sort: Sort) {
        if (!this.clientSortAndPage) {
            this.criteria.sortBy = sort.active;
            this.criteria.sortDirection = sort.direction;
            if (!this.firstTimeSorting) {
                this.loadData();
            }
            this.firstTimeSorting = false;
        }
    }

    private updatePagingSort(pagingSort: PagingSort, firstTimeSort: boolean = true, updatePaginators: boolean = true) {
        if (pagingSort) {
            this.criteria.max = pagingSort.pageSize;
            this.criteria.offset = pagingSort.pageIndex * pagingSort.pageSize;
            this.criteria.sortBy = pagingSort.sortBy;
            this.criteria.sortDirection = pagingSort.sortDirection;
            if (updatePaginators) {
                this.matPaginatorQueryList?.forEach((m) => {
                    m.pageIndex = pagingSort.pageIndex;
                    m.pageSize = pagingSort.pageSize;
                });
            }
            this.searchCriteria = this.searchCriteria ?? {};
            this.searchCriteria.sortDirection = pagingSort.sortDirection as SortDirection;
            this.searchCriteria.sortBy = pagingSort.sortBy;
            if (firstTimeSort) {
                this.firstTimeSort(true);
            }
        }
    }

    private updateCriteria(filter: TableFilter, pagingSort: PagingSort) {
        if (filter) {
            for (const k in filter) {
                this.reflection.setPropertyByString(this.criteria, k, filter[k]);
            }
            this.updatePagingSort(pagingSort);
        }
    }

    private filterUpdated(filter: TableFilter, pagingSort: PagingSort) {
        this.updateCriteria(filter, pagingSort);
        this.loadData(false, pagingSort === null);
    }

    private showColumn(column: ColumnDefinition, initial: boolean): boolean {
        if (column) {
            if (column.showCallBack) {
                if (column.showCallBack.function.call(column.showCallBack.this)) {
                    return true;
                }
                return false;
            } else if (column.postLoadShowCallBack) {
                if (
                    !initial &&
                    column.postLoadShowCallBack.function.call(column.postLoadShowCallBack.this, this.dataSource.data)
                ) {
                    return true;
                }
                return false;
            }
            return true;
        }
        return false;
    }

    private filterColumns(initial: boolean) {
        this.filteredColumns = this.columns.filter((x) => this.showColumn(x, initial));
        this.displayedColumns = Array.from(new Set(this.filteredColumns.map((x) => x.property)));
    }

    updateFromCell($event: TableCellResponse) {
        this.updateElement($event.element);
    }

    createCellCss(column: ColumnDefinition): string {
        if (column.cellCss) {
            const css: string = column.cellCss.join(' ');
            return column.actions ? 'cell-no-padding ' + css : css;
        }
        return column.actions ? 'cell-no-padding' : '';
    }
}
